import type { Signal } from '@preact/signals-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useOdinMemo from '../../common/hooks/use-odin-memo';
import type { ContinuousScrollerFilter } from '../continuous-scroller/continuous-scroller-types';
import Dialog from '../modal-dialog/dialog.component';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';
import type { Api } from './api/api';
import ColumnVisibility from './column-visibility.component';
import { STATUS_INDICATOR_CELL_WIDTH_PX } from './data-grid-functions';
import type { DataGridSettingsMenuItem } from './data-grid-settings-menu.component';
import DataGridSettingsMenu from './data-grid-settings-menu.component';
import type { DataGridColumnSettings } from './data-grid-settings.interface';
import { changeSignalColumnVisibility, updateColumnFilters } from './functions/data-grid-signal-functions';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';

export type DefaultDataGridMenuItems = 'columns' | 'restore' | 'save' | 'manage' | 'import' | 'export';

export interface DataGridCogMenuProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  rowWidth: Signal<number>;
  indicatorRowWidth: Signal<number>;
  filters: Signal<Array<ContinuousScrollerFilter>>;
  onCogMenuItemClicked?: (itemName: string) => void;
  resetView: () => void;
  customSettingsMenuItems?: Array<DataGridSettingsMenuItem>;
  visibleDefaultSettings?: Array<DefaultDataGridMenuItems>;
  showGridColumnsOnSettingsMenu: boolean;
  api: Api;
  autoSizeColumns?: boolean;
  usesIndicatorColumn: boolean;
  showGridMenu?: boolean;
}

export default function DataGridCogMenu({
  columns,
  rowWidth,
  indicatorRowWidth,
  filters,
  onCogMenuItemClicked,
  resetView,
  customSettingsMenuItems,
  visibleDefaultSettings,
  showGridColumnsOnSettingsMenu,
  api,
  autoSizeColumns,
  usesIndicatorColumn,
  showGridMenu,
}: DataGridCogMenuProps) {
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [columnDialogVisible, setColumnDialogVisible] = useState(false);

  const settingsMenuItems = useRef<Array<DataGridSettingsMenuItem>>([]);

  const { t } = useTranslation();

  const defaultSettingsMenuItems: Array<DataGridSettingsMenuItem> = useOdinMemo(() => {
    let allDefaultItems = [
      {
        label: t('restore-view', 'Restore View'),
        key: 'restore',
      },
      {
        key: 'separator',
        isSeparator: true,
      },
      {
        label: t('save-current-view', 'Save Current View'),
        key: 'save',
      },
      {
        label: t('manage-views', 'Manage Views'),
        key: 'manage',
      },
      {
        key: 'separator 2',
        isSeparator: true,
      },
      {
        label: t('import-records', 'Import Records'),
        key: 'import',
      },
      {
        label: t('export-records', 'Export Records'),
        key: 'export',
      },
    ];

    if (visibleDefaultSettings) {
      allDefaultItems = allDefaultItems.filter(
        (item) => visibleDefaultSettings.includes(item.key as DefaultDataGridMenuItems) || item.isSeparator
      );
    }

    if (!showGridColumnsOnSettingsMenu) {
      allDefaultItems.unshift({
        label: t('select-columns', 'Select Columns'),
        key: 'columns',
      });
    }

    return allDefaultItems;
  }, [showGridColumnsOnSettingsMenu, visibleDefaultSettings]);

  useEffect(() => {
    if (customSettingsMenuItems) {
      settingsMenuItems.current = customSettingsMenuItems;
    } else {
      settingsMenuItems.current = defaultSettingsMenuItems;
    }
  }, [customSettingsMenuItems, defaultSettingsMenuItems]);

  const onColumnsVisibilityChanged = (changedColumns: Array<DataGridColumnSettings>) => {
    let totalWidth;
    if (autoSizeColumns) {
      // provide total scroller width so columns can take up the full width if needed
      totalWidth = api.data.getClientWidth() ?? 0;
      if (usesIndicatorColumn) {
        totalWidth -= STATUS_INDICATOR_CELL_WIDTH_PX;
      }
    }

    updateColumnFilters(changedColumns, filters);
    changeSignalColumnVisibility(columns, rowWidth, indicatorRowWidth, changedColumns, totalWidth);
    setColumnDialogVisible(false);
  };

  const onSettingMenuItemClicked = (menuKey: string) => {
    onCogMenuItemClicked && onCogMenuItemClicked(menuKey);
    switch (menuKey) {
      case 'columns':
        setColumnDialogVisible(true);
        break;
      case 'restore':
        resetView();
        break;
    }

    setOverlayVisible(false);
  };

  return (
    <div className="odin-absolute odin-top-0 odin-right-0 odin-flex odin-shrink odin-flex-col p-2">
      <Popover open={overlayVisible} onOpenChange={setOverlayVisible}>
        <PopoverTrigger data-testid="data-grid-settings-menu-trigger">
          <OdinIcon icon="ListFilter" size={OdinIconSize.Small} />
        </PopoverTrigger>
        <PopoverContent side="left" align="start" className="odin-w-96 odin-min-w-min odin-max-w-lg">
          <DataGridSettingsMenu
            items={settingsMenuItems.current}
            onItemClick={onSettingMenuItemClicked}
            columns={columns}
            onColumnsVisibilityChanged={onColumnsVisibilityChanged}
            showGridColumnsOnSettingsMenu={showGridColumnsOnSettingsMenu}
            setOverlayVisible={setOverlayVisible}
            showGridMenu={showGridMenu}
          />
        </PopoverContent>
      </Popover>

      {!showGridColumnsOnSettingsMenu && columnDialogVisible && (
        <Dialog
          header={t('select-columns', 'Select Columns')}
          visible={columnDialogVisible}
          onClose={() => setColumnDialogVisible(false)}
          containerClassName="sm:odin-w-[300px]"
        >
          <ColumnVisibility columns={columns} onColumnsVisibilityChanged={onColumnsVisibilityChanged} />
        </Dialog>
      )}
    </div>
  );
}
